import type { CodexRateLimit } from '@ace-tracker/shared';

/**
 * Codex's quota-headroom visual — a signal no other tool in this dashboard
 * has. Renders the 5-hour ("primary") and weekly ("secondary") window usage
 * as horizontal meters, plus the plan tier and when the reading was taken.
 * `rateLimit.status === 'unavailable'` (collection.agents.codex
 * .includeRateLimits not enabled anywhere in range) renders an explanatory
 * empty state instead of a misleading 0%.
 */
export function CodexRateLimitGauge({ rateLimit }: { rateLimit: CodexRateLimit }) {
  if (rateLimit.status === 'unavailable') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm text-text-muted">Quota headroom not collected.</p>
        <p className="max-w-xs text-xs text-text-faint">
          Enable <code className="font-mono">collection.agents.codex.includeRateLimits</code> in the collector config to see
          5-hour and weekly quota usage here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-center gap-6">
      <Meter label="5-hour window" usedPercent={rateLimit.primaryUsedPercent} />
      <Meter label="Weekly window" usedPercent={rateLimit.secondaryUsedPercent} />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-xs text-text-muted">
        {rateLimit.planType && (
          <span>
            Plan: <span className="font-medium text-text-default">{rateLimit.planType}</span>
          </span>
        )}
        {rateLimit.observedAt && <span>As of {new Date(rateLimit.observedAt).toLocaleString()}</span>}
      </div>
    </div>
  );
}

function Meter({ label, usedPercent }: { label: string; usedPercent?: number }) {
  const value = usedPercent ?? 0;
  const known = usedPercent !== undefined;
  const colorClassName = value >= 90 ? 'bg-error' : value >= 70 ? 'bg-warning' : 'bg-success';

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <p className="text-[11px] uppercase tracking-wide text-text-faint">{label}</p>
        <p className="text-sm font-bold tabular-nums text-text-default">{known ? `${Math.round(value)}%` : '—'}</p>
      </div>
      <div className="h-3 overflow-hidden rounded-full bg-surface-alt">
        <div
          className={`h-full rounded-full ${colorClassName}`}
          style={{ width: `${known ? Math.min(100, Math.max(0, value)) : 0}%` }}
        />
      </div>
    </div>
  );
}
